import type {
  AlreadyLoggedInCacheRead,
  StoredPayload,
  UserInfo,
} from "@/types";

const STORAGE_KEY = "telekomid_already_logged_in_v2";

const MS_PER_MINUTE = 60_000;

// Default already-logged-in cache window (minutes)
export const DEFAULT_CACHE_BREAKER_MIN = 2;

const CACHE_TTL_MIN_MIN = 1;
const CACHE_TTL_MAX_MIN = 60;

const QUERY_CACHE_BREAKER_PARAM = "telekomid_cache_breaker_min";

const logPrefix = "[telekomId] already-logged-in cache";

/**
 * Stable string for matching cache entries. Uses U+0000 (NUL) as a delimiter so
 * `authHost` / `clientId` cannot collide with each other (e.g. a clientId that
 * ends where the next segment would start). It is not sent on the wire—only
 * used as an in-memory / sessionStorage key component.
 */
export const buildAlreadyLoggedInCacheKey = (
  authHost: string,
  clientId: string,
  chatbot: boolean,
) => `${authHost}\0${clientId}\0${chatbot ? "1" : "0"}`;

const clampMinutes = (minutes: number): number =>
  Math.min(Math.max(minutes, CACHE_TTL_MIN_MIN), CACHE_TTL_MAX_MIN);

const minutesToMs = (min: number): number => Math.round(min * MS_PER_MINUTE);

/**
 * Cache TTL in ms. Precedence: URL `?telekomid_cache_breaker_min=` (minutes), then
 * `cacheBreakerMin` (minutes from config / `data-cache-breaker-min`, already parsed),
 * then {@link DEFAULT_CACHE_BREAKER_MIN}. `0` disables caching.
 */
export const resolveCacheBreakerMs = (cacheBreakerMin?: number): number => {
  if (typeof window !== "undefined") {
    try {
      const raw = new URLSearchParams(window.location.search).get(
        QUERY_CACHE_BREAKER_PARAM,
      );
      if (raw != null && raw !== "") {
        const n = Number.parseInt(raw, 10);
        if (!Number.isNaN(n)) {
          if (n === 0) return 0;
          return minutesToMs(clampMinutes(n));
        }
      }
    } catch (e) {
      console.debug(`${logPrefix}: failed to read cache breaker from query`, e);
    }
  }

  if (cacheBreakerMin !== undefined && Number.isFinite(cacheBreakerMin)) {
    if (cacheBreakerMin === 0) return 0;
    return minutesToMs(clampMinutes(cacheBreakerMin));
  }

  return minutesToMs(DEFAULT_CACHE_BREAKER_MIN);
};

const parsePayload = (raw: string): StoredPayload | null => {
  try {
    const data = JSON.parse(raw) as Record<string, unknown>;
    if (
      typeof data.key !== "string" ||
      typeof data.expiresAt !== "number" ||
      !Number.isFinite(data.expiresAt)
    ) {
      return null;
    }
    if (data.outcome === "unauthorized") {
      return {
        key: data.key,
        expiresAt: data.expiresAt,
        outcome: "unauthorized",
      };
    }
    const user = data.user;
    if (
      user &&
      typeof user === "object" &&
      typeof (user as UserInfo).guid === "string"
    ) {
      return {
        key: data.key,
        expiresAt: data.expiresAt,
        outcome: "user",
        user: user as UserInfo,
      };
    }
    return null;
  } catch {
    return null;
  }
};

const readPayload = (): StoredPayload | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return parsePayload(raw);
  } catch (e) {
    console.debug(`${logPrefix}: read failed (getItem/parse)`, e);
    return null;
  }
};

export const readAlreadyLoggedInCache = (
  authHost: string,
  clientId: string,
  chatbot: boolean,
): AlreadyLoggedInCacheRead => {
  const key = buildAlreadyLoggedInCacheKey(authHost, clientId, chatbot);
  const payload = readPayload();
  if (!payload || payload.key !== key) return { kind: "miss" };
  if (Date.now() >= payload.expiresAt) {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.debug(`${logPrefix}: removeItem (expired) failed`, e);
    }
    return { kind: "miss" };
  }
  if (payload.outcome === "unauthorized") {
    return { kind: "unauthorized" };
  }
  return { kind: "user", user: { ...payload.user } };
};

export const writeAlreadyLoggedInCache = (
  authHost: string,
  clientId: string,
  chatbot: boolean,
  user: UserInfo,
  ttlMs: number,
) => {
  if (ttlMs <= 0) return;

  const key = buildAlreadyLoggedInCacheKey(authHost, clientId, chatbot);
  const payload: StoredPayload = {
    key,
    expiresAt: Date.now() + ttlMs,
    outcome: "user",
    user: { ...user },
  };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (e) {
    console.error(`${logPrefix}: setItem failed`, e);
  }
};

export const writeAlreadyLoggedInUnauthorizedCache = (
  authHost: string,
  clientId: string,
  chatbot: boolean,
  ttlMs: number,
) => {
  if (ttlMs <= 0) return;

  const key = buildAlreadyLoggedInCacheKey(authHost, clientId, chatbot);
  const payload: StoredPayload = {
    key,
    expiresAt: Date.now() + ttlMs,
    outcome: "unauthorized",
  };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (e) {
    console.error(`${logPrefix}: setItem failed (401 cache)`, e);
  }
};

export const clearAlreadyLoggedInCache = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.debug(`${logPrefix}: clear removeItem failed`, e);
  }
};
